import { useState, useEffect, useCallback } from 'react';
import {
  decodeAmountFromCalldata,
  getExplorerApiConfig,
  methodFromSelector,
  type ExplorerApiConfig,
} from '@/lib/explorerApi';

export interface BlockscoutTransaction {
  hash: string;
  timestamp: string;
  method: string | null;
  to: { hash: string } | null;
  from: { hash: string };
  status: string;
  value: string; // native token amount in wei (always present in Blockscout API)
  decoded_input: {
    parameters: Array<{ name: string; type: string; value: string }>;
  } | null;
}

interface UseBlockscoutTransactionsResult {
  transactions: BlockscoutTransaction[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

const PAGE_CAP = 20;
const ETHERSCAN_PAGE_SIZE = 100;

interface BlockscoutApiTx {
  hash: string;
  timestamp: string;
  method: string | null;
  to: { hash: string } | null;
  from: { hash: string };
  status: string;
  value: string;
  raw_input?: string;
  decoded_input: BlockscoutTransaction['decoded_input'];
}

function withCalldataAmount(
  method: string | null,
  input: string | null | undefined,
  fallbackDecoded: BlockscoutTransaction['decoded_input'],
): BlockscoutTransaction['decoded_input'] {
  const amount = decodeAmountFromCalldata(method, input);
  if (amount != null) {
    return { parameters: [{ name: 'amount', type: 'uint256', value: amount }] };
  }
  return fallbackDecoded;
}

function normalizeBlockscoutTx(tx: BlockscoutApiTx): BlockscoutTransaction {
  const method = tx.method;
  return {
    hash: tx.hash,
    timestamp: tx.timestamp,
    method,
    to: tx.to,
    from: tx.from,
    status: tx.status,
    value: tx.value ?? '0',
    decoded_input: withCalldataAmount(method, tx.raw_input, tx.decoded_input),
  };
}

// The API only accepts filter=to or filter=from individually (not "to | from").
// We fetch both directions and deduplicate by hash.
async function fetchFilteredBlockscoutTransactions(
  explorerBaseUrl: string,
  bridgeAddress: string,
  filter: 'to' | 'from',
): Promise<BlockscoutTransaction[]> {
  const results: BlockscoutTransaction[] = [];
  let nextPageParams: Record<string, string | number> | null = null;
  let pageCount = 0;

  do {
    const url = new URL(`${explorerBaseUrl}/api/v2/addresses/${bridgeAddress}/transactions`);
    url.searchParams.set('filter', filter);
    if (nextPageParams) {
      for (const [key, value] of Object.entries(nextPageParams)) {
        url.searchParams.set(key, String(value));
      }
    }

    const res = await fetch(url.toString());
    if (!res.ok) {
      throw new Error(`Blockscout API error: ${res.status}`);
    }

    const data = await res.json();
    const items = (data.items ?? []) as BlockscoutApiTx[];
    results.push(...items.map(normalizeBlockscoutTx));
    nextPageParams = data.next_page_params ?? null;
    pageCount++;
  } while (nextPageParams !== null && pageCount < PAGE_CAP);

  return results;
}

async function fetchBlockscoutTransactions(
  explorerBaseUrl: string,
  bridgeAddress: string,
): Promise<BlockscoutTransaction[]> {
  const [toTxs, fromTxs] = await Promise.all([
    fetchFilteredBlockscoutTransactions(explorerBaseUrl, bridgeAddress, 'to'),
    fetchFilteredBlockscoutTransactions(explorerBaseUrl, bridgeAddress, 'from'),
  ]);

  const seen = new Set<string>();
  const merged: BlockscoutTransaction[] = [];
  for (const tx of [...toTxs, ...fromTxs]) {
    if (!seen.has(tx.hash)) {
      seen.add(tx.hash);
      merged.push(tx);
    }
  }
  return merged;
}

interface EtherscanTx {
  hash: string;
  timeStamp: string;
  from: string;
  to: string;
  value: string;
  input: string;
  methodId?: string;
  functionName?: string;
  isError?: string;
  txreceipt_status?: string;
}

function normalizeEtherscanTx(tx: EtherscanTx): BlockscoutTransaction {
  const selector =
    tx.methodId ||
    (tx.input && tx.input.length >= 10 ? tx.input.slice(0, 10) : null);
  const mapped = methodFromSelector(selector);
  const fromName = tx.functionName ? tx.functionName.split('(')[0] || null : null;
  // Prefer a mapped/ABI name; mark unknown selectors as "other" so the
  // null-method direction heuristic does not treat admin calls as deposits.
  const method = mapped || fromName || (selector ? 'other' : null);

  const failed = tx.isError === '1' || tx.txreceipt_status === '0';

  return {
    hash: tx.hash,
    timestamp: new Date(Number(tx.timeStamp) * 1000).toISOString(),
    method,
    to: tx.to ? { hash: tx.to } : null,
    from: { hash: tx.from },
    status: failed ? 'error' : 'ok',
    value: tx.value ?? '0',
    decoded_input: withCalldataAmount(method, tx.input, null),
  };
}

async function fetchEtherscanTransactions(
  apiBaseUrl: string,
  bridgeAddress: string,
): Promise<BlockscoutTransaction[]> {
  const results: BlockscoutTransaction[] = [];
  let page = 1;

  while (page <= PAGE_CAP) {
    const url = new URL(apiBaseUrl);
    url.searchParams.set('module', 'account');
    url.searchParams.set('action', 'txlist');
    url.searchParams.set('address', bridgeAddress);
    url.searchParams.set('page', String(page));
    url.searchParams.set('offset', String(ETHERSCAN_PAGE_SIZE));
    url.searchParams.set('sort', 'desc');

    const res = await fetch(url.toString());
    if (!res.ok) {
      throw new Error(`Explorer API error: ${res.status}`);
    }

    const data = await res.json();
    if (data.status === '0' && String(data.message || '').toLowerCase().includes('no transaction')) {
      break;
    }
    if (data.status !== '1' || !Array.isArray(data.result)) {
      throw new Error(data.result || data.message || 'Explorer txlist failed');
    }

    const batch = data.result as EtherscanTx[];
    results.push(...batch.map(normalizeEtherscanTx));
    if (batch.length < ETHERSCAN_PAGE_SIZE) break;
    page++;
  }

  return results;
}

async function fetchAllTransactions(
  config: ExplorerApiConfig,
  bridgeAddress: string,
): Promise<BlockscoutTransaction[]> {
  if (config.kind === 'etherscan') {
    return fetchEtherscanTransactions(config.apiBaseUrl, bridgeAddress);
  }
  return fetchBlockscoutTransactions(config.apiBaseUrl, bridgeAddress);
}

export function useBlockscoutTransactions(
  chainId: number | null,
  bridgeAddress: string,
): UseBlockscoutTransactionsResult {
  const [transactions, setTransactions] = useState<BlockscoutTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (chainId == null || !bridgeAddress) {
      setTransactions([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const config = getExplorerApiConfig(chainId);

    // Drop previous chain/address rows immediately so charts don't flash stale data.
    setTransactions([]);
    setIsLoading(true);
    setError(null);

    fetchAllTransactions(config, bridgeAddress)
      .then((txs) => {
        if (!cancelled) {
          setTransactions(txs);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [chainId, bridgeAddress, retryCount]);

  const retry = useCallback(() => {
    setRetryCount((c) => c + 1);
  }, []);

  return { transactions, isLoading, error, retry };
}
